import { ReactNode, useEffect } from 'react';
import { Navigate, useLocation } from 'react-router-dom';
import { Spin } from 'antd';
import { useAuthStore } from '../../stores/authStore';

interface ProtectedRouteProps {
  children: ReactNode;
  requiredRole?: string;
}

const ProtectedRoute = ({ children, requiredRole }: ProtectedRouteProps) => {
  const location = useLocation();
  const { 
    isAuthenticated, 
    isLoading, 
    user, 
    loadUserProfile,
    logout
  } = useAuthStore();

  // 初始化時載入用戶資料
  useEffect(() => {
    const token = localStorage.getItem('authToken');
    if (token && !user && !isLoading) {
      loadUserProfile();
    }
  }, [user, isLoading, loadUserProfile]);

  // 如果沒有 token，確保重置認證狀態，避免重複跳轉
  const token = typeof window !== 'undefined' ? localStorage.getItem('authToken') : null;
  useEffect(() => {
    if (!token && isAuthenticated) {
      logout();
    }
  }, [token, isAuthenticated, logout]);

  // 正在載入中
  if (isLoading) {
    return (
      <div style={{
        display: 'flex',
        justifyContent: 'center',
        alignItems: 'center',
        height: '100vh'
      }}>
        <Spin size="large" tip="載入中..." />
      </div>
    );
  }

  // 未認證，重定向到登錄頁
  if (!isAuthenticated || !token) {
    const base = ((import.meta as any)?.env?.BASE_URL) || '/';
    const to = (base?.endsWith('/') ? base : `${base}/`) + 'login';
    return <Navigate to={to} state={{ from: location }} replace />;
  }

  // 檢查角色權限
  if (requiredRole && user?.role !== requiredRole && user?.role !== 'ADMIN') {
    return (
      <div style={{
        display: 'flex',
        justifyContent: 'center',
        alignItems: 'center',
        height: '100vh',
        flexDirection: 'column'
      }}>
        <h3>權限不足</h3>
        <p>您沒有權限訪問此頁面</p>
      </div>
    );
  }

  // 如果是 PICK 角色且當前不在 PICK 專屬路由中，則重定向到 PICK 專屬頁面
  if (user?.role === 'PICK_SCANNING' && !location.pathname.startsWith('/pick')) {
    return <Navigate to="/pick/inventory/picking" state={{ from: location }} replace/>;
  }

  // 如果是 PACK 角色且當前不在 PACK 專屬路由中，則重定向到 PACK 專屬頁面
  if (user?.role === 'PACK_SCANNING' && !location.pathname.startsWith('/pack')) {
    return <Navigate to="/pack/inventory/packing" state={{ from: location }} replace/>;
  }
  if (user?.role === 'PICKUP_SCANNING' && !location.pathname.startsWith('/pickup')) {
    return <Navigate to="/pickup/inventory/packing" state={{ from: location }} replace/>;
  }
  return <>{children}</>;
};

export default ProtectedRoute;